import { Pencil, UserCheck } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import type { TicketDetail } from '@aurora/contracts';
import { absoluteTime, relativeTime, shortDate } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useDepartments, useGroups, usePriorities } from '../../lib/directory';
import { PersonPicker } from '../../components/person-picker';
import { describeError, useToast } from '../../components/toast';
import { Avatar, Badge, Button, Select } from '../../components/ui';
import {
  CustomFieldInputs,
  CustomFieldSummary,
  isUnanswered,
  useTicketFormFields,
  type TicketFormField,
} from './custom-fields';
import { useTicketChoices, useTicketUpdate } from './detail-data';
import { DEFAULT_LEVEL, LEVEL_CHOICES, levelLabel, matrixPriority, type Level } from './priority-matrix';

/**
 * A ticket's properties, editable where they are shown.
 *
 * This panel only displayed values: an agent could not assign a ticket to
 * themselves, raise its priority, move it to another group or fill in a custom
 * field anywhere on the screen, and the bulk bar that could needs a permission
 * the Agent role does not hold. Each property is now its own control, and each
 * change is sent on its own, shown at once, and put back with the server's
 * reason if it is refused.
 */

function Row({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] items-center gap-2 py-1.5">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-mini font-medium text-ink-muted">
          {label}
        </label>
      ) : (
        <span className="text-mini font-medium text-ink-muted">{label}</span>
      )}
      <div className="min-w-0 text-body text-ink">{children}</div>
    </div>
  );
}

export function PropertiesPanel({ ticket, locked = false }: { ticket: TicketDetail; locked?: boolean }) {
  const { session, can } = useSession();
  // A merged ticket is closed and its work goes on elsewhere: assigning it or
  // raising its priority would notify people about a ticket nobody works.
  const editable = can('ticket.edit') && !locked;
  const canAssign = editable && can('ticket.assign');
  const number = String(ticket.number);
  const update = useTicketUpdate(number);
  const groups = useGroups(editable);
  const priorities = usePriorities(editable);
  const departments = useDepartments(editable);
  const choices = useTicketChoices(editable);
  const toast = useToast();
  const matrix = useTicketFormFields(ticket.number).data?.meta?.priority_matrix ?? null;
  const ids = {
    assignee: useId(),
    group: useId(),
    priority: useId(),
    type: useId(),
    department: useId(),
    urgency: useId(),
    impact: useId(),
    follows: useId(),
  };
  const me = session?.user;
  const urgency = (ticket.urgency ?? DEFAULT_LEVEL) as Level;
  const impact = (ticket.impact ?? DEFAULT_LEVEL) as Level;

  const assign = (person: { id: number; name: string } | null) => {
    update.mutate({
      body: { assignee_id: person?.id ?? null },
      optimistic: { assignee: person ? { id: person.id, name: person.name } : null },
      success: person ? (person.id === me?.id ? 'Assigned to you' : `Assigned to ${person.name}`) : 'Unassigned',
    });
  };

  /**
   * Urgency or impact, and the priority that follows from the grid.
   *
   * The priority the grid gives is shown at once, beside the new value; the
   * server works it out again and its answer replaces the guess. The toast
   * reports what the server actually did — including a priority that did not
   * move because the grid is off or names nothing for the pair.
   */
  const changeLevel = (field: 'urgency' | 'impact', value: Level) => {
    const followed =
      field === 'urgency' ? matrixPriority(matrix, value, impact) : matrixPriority(matrix, urgency, value);
    const priority = followed ? priorities.data?.find((candidate) => candidate.key === followed) : undefined;
    const before = ticket.priority.key;
    const optimistic: Partial<TicketDetail> = field === 'urgency' ? { urgency: value } : { impact: value };
    if (priority && priority.key !== before) {
      optimistic.priority = { ...ticket.priority, key: priority.key, label: priority.name, level: priority.level };
    }
    update.mutate(
      { body: { [field]: value }, optimistic },
      {
        onSuccess: (response) => {
          const moved = response.data.priority.key !== before;
          toast.success(
            `${field === 'urgency' ? 'Urgency' : 'Impact'} set to ${levelLabel(value)}`,
            moved ? `The priority is now ${response.data.priority.label}.` : undefined,
          );
        },
      },
    );
  };

  return (
    <section aria-labelledby="properties-heading">
      <h2 id="properties-heading" className="mb-1 text-reading font-semibold text-ink">
        Properties
      </h2>

      <div className="divide-y divide-line">
        <Row label="Requester">
          {ticket.requester ? (
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <Avatar name={ticket.requester.name} size="xs" />
                <bdi className="truncate font-medium">{ticket.requester.name}</bdi>
                {ticket.requester.is_vip && <Badge tone="caution">VIP</Badge>}
              </div>
              {ticket.requester.email && (
                <a
                  href={`mailto:${ticket.requester.email}`}
                  className="block truncate text-mini text-ink-subtle hover:text-accent-ink hover:underline"
                >
                  <bdi>{ticket.requester.email}</bdi>
                </a>
              )}
            </div>
          ) : (
            <span className="text-ink-subtle">—</span>
          )}
        </Row>

        <Row label="Assignee" htmlFor={canAssign ? ids.assignee : undefined}>
          {canAssign ? (
            <div className="space-y-1">
              <PersonPicker
                id={ids.assignee}
                source="agents"
                size="sm"
                value={ticket.assignee}
                onChange={assign}
                groupId={ticket.group?.id ?? null}
                clearable
                clearLabel="Unassigned"
                placeholder={ticket.group ? `Search ${ticket.group.name}…` : 'Search agents…'}
                disabled={update.isPending}
              />
              {me && ticket.assignee?.id !== me.id && (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => assign({ id: me.id, name: me.name })}
                  disabled={update.isPending}
                >
                  <UserCheck aria-hidden className="size-3.5" />
                  Assign to me
                </Button>
              )}
            </div>
          ) : ticket.assignee ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <Avatar name={ticket.assignee.name} size="xs" />
              <bdi className="truncate">{ticket.assignee.name}</bdi>
            </span>
          ) : (
            <Badge tone="caution">Unassigned</Badge>
          )}
        </Row>

        <Row label="Group" htmlFor={editable ? ids.group : undefined}>
          {editable ? (
            <Select
              id={ids.group}
              size="sm"
              value={ticket.group ? String(ticket.group.id) : ''}
              disabled={update.isPending || groups.isLoading}
              onChange={(event) => {
                const id = event.target.value ? Number(event.target.value) : null;
                const group = groups.data?.find((candidate) => candidate.id === id) ?? null;
                update.mutate({
                  body: { group_id: id },
                  optimistic: { group: group ? { id: group.id, name: group.name } : null },
                  success: group ? `Moved to ${group.name}` : 'Removed from its group',
                });
              }}
            >
              <option value="">No group</option>
              {/* The current group stays choosable even if it is no longer listed. */}
              {ticket.group && !groups.data?.some((group) => group.id === ticket.group!.id) && (
                <option value={ticket.group.id}>{ticket.group.name}</option>
              )}
              {(groups.data ?? []).map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          ) : (
            <bdi>{ticket.group?.name ?? '—'}</bdi>
          )}
        </Row>

        <Row label="Priority" htmlFor={editable ? ids.priority : undefined}>
          {editable ? (
            <Select
              id={ids.priority}
              size="sm"
              value={ticket.priority.key}
              disabled={update.isPending || priorities.isLoading}
              onChange={(event) => {
                const chosen = priorities.data?.find((priority) => priority.key === event.target.value);
                if (!chosen) return;
                update.mutate({
                  body: { priority: chosen.key },
                  optimistic: { priority: { ...ticket.priority, key: chosen.key, label: chosen.name, level: chosen.level } },
                  success: `Priority set to ${chosen.name}`,
                });
              }}
            >
              {!priorities.data?.some((priority) => priority.key === ticket.priority.key) && (
                <option value={ticket.priority.key}>{ticket.priority.label}</option>
              )}
              {(priorities.data ?? []).map((priority) => (
                <option key={priority.key} value={priority.key}>
                  {priority.name}
                </option>
              ))}
            </Select>
          ) : (
            ticket.priority.label
          )}
        </Row>

        <Row label="Urgency" htmlFor={editable ? ids.urgency : undefined}>
          {editable ? (
            <Select
              id={ids.urgency}
              size="sm"
              value={String(urgency)}
              disabled={update.isPending}
              aria-describedby={matrix?.enabled ? ids.follows : undefined}
              onChange={(event) => changeLevel('urgency', Number(event.target.value) as Level)}
            >
              {LEVEL_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </Select>
          ) : (
            (levelLabel(urgency) ?? '—')
          )}
        </Row>

        <Row label="Impact" htmlFor={editable ? ids.impact : undefined}>
          {editable ? (
            <div className="space-y-1">
              <Select
                id={ids.impact}
                size="sm"
                value={String(impact)}
                disabled={update.isPending}
                aria-describedby={matrix?.enabled ? ids.follows : undefined}
                onChange={(event) => changeLevel('impact', Number(event.target.value) as Level)}
              >
                {LEVEL_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </Select>
              {matrix?.enabled && (
                <p id={ids.follows} className="text-mini text-ink-subtle">
                  Changing urgency or impact sets the priority to match. You can still choose a priority yourself.
                </p>
              )}
            </div>
          ) : (
            (levelLabel(impact) ?? '—')
          )}
        </Row>

        <Row label="Type" htmlFor={editable && (choices.data?.type.length ?? 0) > 0 ? ids.type : undefined}>
          {editable && (choices.data?.type.length ?? 0) > 0 ? (
            <Select
              id={ids.type}
              size="sm"
              value={ticket.type ?? ''}
              disabled={update.isPending}
              onChange={(event) => {
                const chosen = choices.data?.type.find((type) => type.value === event.target.value);
                if (!chosen) return;
                update.mutate({
                  body: { type: chosen.value },
                  optimistic: { type: chosen.value, type_label: chosen.label },
                  success: `Type set to ${chosen.label}`,
                });
              }}
            >
              {!ticket.type && <option value="">—</option>}
              {(choices.data?.type ?? []).map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          ) : (
            (ticket.type_label ?? ticket.type ?? '—')
          )}
        </Row>

        <Row label="Department" htmlFor={editable ? ids.department : undefined}>
          {editable ? (
            <Select
              id={ids.department}
              size="sm"
              value={ticket.department ? String(ticket.department.id) : ''}
              disabled={update.isPending || departments.isLoading}
              onChange={(event) => {
                const id = event.target.value ? Number(event.target.value) : null;
                const department = departments.data?.find((candidate) => candidate.id === id) ?? null;
                update.mutate({
                  body: { department_id: id },
                  optimistic: { department: department ? { id: department.id, name: department.name } : null },
                  success: department ? `Department set to ${department.name}` : 'Department cleared',
                });
              }}
            >
              <option value="">None</option>
              {ticket.department && !departments.data?.some((d) => d.id === ticket.department!.id) && (
                <option value={ticket.department.id}>{ticket.department.name}</option>
              )}
              {(departments.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          ) : (
            <bdi>{ticket.department?.name ?? '—'}</bdi>
          )}
        </Row>

        <Row label="Source">{ticket.source_label ?? ticket.source ?? '—'}</Row>

        <Row label="Raised">
          <span title={absoluteTime(ticket.created_at)}>{shortDate(ticket.created_at)}</span>
        </Row>
        <Row label="Last activity">
          <span title={absoluteTime(ticket.last_activity_at)}>{relativeTime(ticket.last_activity_at)}</span>
        </Row>
        {ticket.resolved_at && (
          <Row label="Resolved">
            <span title={absoluteTime(ticket.resolved_at)}>{shortDate(ticket.resolved_at)}</span>
          </Row>
        )}
      </div>

      {ticket.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" aria-label="Tags">
          {ticket.tags.map((tag) => (
            <Badge key={tag}>
              <bdi>{tag}</bdi>
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}

/** What a requester needs to know about their own request — no desk jargon. */
export function RequesterFacts({ ticket }: { ticket: TicketDetail }) {
  return (
    <section aria-labelledby="facts-heading">
      <h2 id="facts-heading" className="mb-1 text-reading font-semibold text-ink">
        Your request
      </h2>
      <div className="divide-y divide-line">
        <Row label="Status">{ticket.status.requester_label ?? ticket.status.label}</Row>
        <Row label="With">
          {ticket.assignee ? <bdi>{ticket.assignee.name}</bdi> : <span className="text-ink-muted">Waiting for an agent</span>}
        </Row>
        <Row label="Reference">#{ticket.number}</Row>
        <Row label="Raised">
          <span title={absoluteTime(ticket.created_at)}>{shortDate(ticket.created_at)}</span>
        </Row>
        <Row label="Last update">
          <span title={absoluteTime(ticket.last_activity_at)}>{relativeTime(ticket.last_activity_at)}</span>
        </Row>
        {ticket.resolved_at && (
          <Row label="Resolved">
            <span title={absoluteTime(ticket.resolved_at)}>{shortDate(ticket.resolved_at)}</span>
          </Row>
        )}
      </div>
    </section>
  );
}

/**
 * The answers to whatever the form asked, and a way to change them.
 *
 * A request that needed a field filled in before it could be resolved had no
 * way to fill it in after it was raised. Fields still needed to resolve are
 * flagged here, so the agent finds out before pressing Resolve rather than
 * after.
 */
export function CustomFieldsCard({
  ticket,
  fields,
  loading,
  locked = false,
}: {
  ticket: TicketDetail;
  fields: TicketFormField[];
  loading?: boolean;
  /** Read-only whatever the permissions — a merged ticket. */
  locked?: boolean;
}) {
  const { session, can } = useSession();
  const isAgent = Boolean(session?.is_agent);
  const editable = isAgent && can('ticket.edit') && !locked && fields.some((field) => field.editable);
  const update = useTicketUpdate(String(ticket.number));
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const stored = ticket.custom_fields ?? {};

  if (loading || fields.length === 0) return null;

  const answered = fields.some((field) => !isUnanswered(stored[field.key]));
  const neededToResolve = isAgent
    ? fields.filter((field) => field.required_to_close && isUnanswered(stored[field.key]))
    : [];
  if (!answered && neededToResolve.length === 0 && !editable) return null;

  const start = () => {
    setValues({ ...stored });
    setErrors({});
    setFailure(null);
    setEditing(true);
  };

  const save = () => {
    // Only what changed: re-sending an answer nobody touched would put a change
    // on the timeline that never happened.
    const changed = Object.fromEntries(
      fields
        .filter((field) => field.editable)
        .map((field) => field.key)
        .filter((key) => JSON.stringify(normalise(values[key])) !== JSON.stringify(normalise(stored[key])))
        .map((key) => [key, normalise(values[key])]),
    );
    if (Object.keys(changed).length === 0) {
      setEditing(false);
      return;
    }
    setFailure(null);
    update.mutate(
      { body: { custom_fields: changed }, silent: true, success: 'Details saved' },
      {
        onSuccess: () => setEditing(false),
        onError: (error) => {
          setErrors(error.fieldErrors);
          setFailure(describeError(error).title);
        },
      },
    );
  };

  return (
    <section aria-labelledby="details-heading">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 id="details-heading" className="text-reading font-semibold text-ink">
          Details
        </h2>
        {editable && !editing && (
          <Button size="xs" variant="ghost" onClick={start}>
            <Pencil aria-hidden className="size-3" />
            Edit
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          {failure && (
            <p role="alert" className="rounded-control border border-critical-line bg-critical-soft px-3 py-2 text-body text-critical-ink">
              {failure}
            </p>
          )}
          <CustomFieldInputs
            fields={fields}
            values={values}
            errors={errors}
            idPrefix="detail-cf"
            onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" loading={update.isPending} onClick={save}>
              Save details
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <CustomFieldSummary fields={fields} values={stored} />
          {neededToResolve.length > 0 && (
            <ul className="space-y-1">
              {neededToResolve.map((field) => (
                <li key={field.key} className="flex items-center justify-between gap-2 text-body">
                  <span dir="auto" className="text-ink-muted">
                    {field.label}
                  </span>
                  <Badge tone="caution">Needed to resolve</Badge>
                </li>
              ))}
            </ul>
          )}
          {!answered && neededToResolve.length === 0 && (
            <p className="text-body text-ink-muted">Nothing filled in yet.</p>
          )}
        </div>
      )}
    </section>
  );
}

function normalise(value: unknown): unknown {
  return isUnanswered(value) ? null : value;
}
